import * as React from "react";
import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useHistory } from "react-router-dom";
import {
  Badge,
  Button,
  Card,
  CardHeader,
  Input,
  InputGroup,
  InputGroupAddon,
  InputGroupText,
  Progress,
} from "reactstrap";
import { toast } from "react-toastify";

import Loader from "../../../common/Loader";
import NoDataMessage from "../../../common/NoDataMessage";
import MenuBar from "../../../components/MenuBar";
import { Date as DateDisplay } from "../../../components/Date";
import {
  ContentBatchRow,
  ContentPlatform,
  ContentQuestionRow,
} from "../../../common/contentTypes";
import * as Content from "../../../services/api/Content";
import { ConfirmModal } from "../ConfirmModal";
import { ContentTable } from "../ContentTable";
import {
  effectiveGenerationStatus,
  generationFailureText,
  generationProgress,
  isRetryable,
} from "../batchStatus";

const POLL_INTERVAL_MS = 3_000;
const PLATFORMS: ContentPlatform[] = ["slack", "teams", "events"];

const STATUS_COLOR: Record<string, string> = {
  running: "info",
  done: "success",
  failed: "danger",
  stalled: "danger",
};

const REVIEW_BADGE: Record<string, string> = {
  approved: "success",
  rejected: "danger",
  pending: "secondary",
};

export const ContentDraftsPage: React.FC = () => {
  const history = useHistory();
  const [batches, setBatches] = useState<ContentBatchRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [busyId, setBusyId] = useState<string | null>(null);
  const [renaming, setRenaming] = useState<{ id: string; name: string } | null>(
    null,
  );
  const [expanded, setExpanded] = useState<string | null>(null);
  const [expandedQuestions, setExpandedQuestions] = useState<
    ContentQuestionRow[]
  >([]);
  const [deleting, setDeleting] = useState<ContentBatchRow | null>(null);

  const batchesRef = useRef<ContentBatchRow[]>([]);
  batchesRef.current = batches;

  /**
   * The edit in progress, mirrored so `finishRename` sees it synchronously.
   * `setRenaming(null)` does not update the handler already bound to the input,
   * so a trailing blur would otherwise re-enter with the edit still pending —
   * saving a rename Escape had just cancelled, or sending it twice after Enter.
   */
  const renamingRef = useRef<{ id: string; name: string } | null>(null);
  renamingRef.current = renaming;

  const load = useCallback(async (showSpinner: boolean) => {
    if (showSpinner) {
      setLoading(true);
    }
    try {
      // Drafts is the unpublished queue, so it asks the server for drafts only
      // rather than filtering a full list client-side — a published set has
      // nothing left to do here and only makes the queue harder to scan.
      const { items } = await Content.listBatches({ state: "draft" });
      setBatches(items ?? []);
    } catch (error) {
      // A silent poll failure would freeze the progress numbers with no hint
      // that they had stopped updating.
      toast.error((error as Error).message || "Could not load drafts");
    } finally {
      if (showSpinner) {
        setLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    void load(true);
  }, [load]);

  const anyRunning = batches.some(
    (batch) => effectiveGenerationStatus(batch.generation) === "running",
  );

  useEffect(() => {
    // Never poll when idle: a finished or stalled list has nothing to update,
    // and this tab can sit open all day.
    if (!anyRunning) {
      return undefined;
    }
    const timer = setInterval(() => void load(false), POLL_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [anyRunning, load]);

  const visible = search.trim()
    ? batches.filter((batch) =>
        batch.name.toLowerCase().includes(search.trim().toLowerCase()),
      )
    : batches;

  const toggleExpanded = async (batch: ContentBatchRow) => {
    if (expanded === batch.id) {
      setExpanded(null);
      return;
    }
    setExpanded(batch.id);
    setExpandedQuestions([]);
    try {
      const { items } = await Content.listQuestions({
        batchId: batch.id,
        limit: 100,
      });
      setExpandedQuestions(items ?? []);
    } catch (error) {
      toast.error((error as Error).message || "Could not load the questions");
    }
  };

  const finishRename = async (commit: boolean) => {
    const pending = renamingRef.current;
    renamingRef.current = null;
    setRenaming(null);

    if (!commit || !pending) {
      return;
    }
    const current = batchesRef.current.find((batch) => batch.id === pending.id);
    if (!pending.name.trim() || pending.name.trim() === current?.name) {
      return;
    }

    try {
      await Content.renameBatch({
        batchId: pending.id,
        name: pending.name.trim(),
      });
      await load(false);
    } catch (error) {
      toast.error((error as Error).message || "Could not rename");
    }
  };

  const act = async (
    batch: ContentBatchRow,
    run: () => Promise<void>,
    failure: string,
  ) => {
    setBusyId(batch.id);
    try {
      await run();
      await load(false);
    } catch (error) {
      toast.error((error as Error).message || failure);
    } finally {
      setBusyId(null);
    }
  };

  const confirmDelete = async () => {
    const batch = deleting;
    setDeleting(null);
    if (!batch) {
      return;
    }
    await act(
      batch,
      async () => {
        const { deletedCount } = await Content.deleteBatch(batch.id);
        toast.success(
          `Deleted “${batch.name}” and ${deletedCount} question${
            deletedCount === 1 ? "" : "s"
          }`,
        );
      },
      "Could not delete the draft",
    );
  };

  return (
    <>
      <MenuBar />
      <Card>
        <CardHeader className="d-flex align-items-center">
          <h2 className="h5 mb-0">Drafts</h2>
          {anyRunning && (
            <Badge color="info" className="ml-2">
              <i className="fas fa-circle-notch fa-spin mr-1" />
              live
            </Badge>
          )}
          <div className="ml-auto d-flex align-items-center">
            <InputGroup size="sm" style={{ width: 240 }} className="mr-2">
              <InputGroupAddon addonType="prepend">
                <InputGroupText>
                  <i className="fas fa-search" />
                </InputGroupText>
              </InputGroupAddon>
              <Input
                value={search}
                placeholder="Search batch names…"
                onChange={(e) => setSearch(e.target.value)}
              />
            </InputGroup>
            <Button
              size="sm"
              color="primary"
              onClick={() => history.push("/content/generate")}
            >
              <i className="fas fa-plus mr-1" />
              Generate
            </Button>
          </div>
        </CardHeader>

        {loading ? (
          <Loader />
        ) : visible.length === 0 ? (
          <NoDataMessage light componentName="batch" />
        ) : (
          <ContentTable className="table table-hover mb-0">
            <thead>
              <tr>
                <th style={{ width: 28 }} />
                <th>Batch name</th>
                <th>Type</th>
                <th>Created</th>
                <th>Questions</th>
                <th style={{ minWidth: 190 }}>Review progress</th>
                <th>State</th>
                <th className="text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {visible.map((batch) => {
                const status = effectiveGenerationStatus(batch.generation);
                const progress = generationProgress(batch.generation);
                const failure = generationFailureText(batch.generation, status);
                const reviewed = batch.approvedCount + batch.rejectedCount;
                const total = batch.questionIds.length;
                const fullyReviewed = total > 0 && reviewed === total;
                const busy = busyId === batch.id;
                const isOpen = expanded === batch.id;

                return (
                  <React.Fragment key={batch.id}>
                    <tr>
                      <td>
                        <Button
                          color="link"
                          className="p-0"
                          title={isOpen ? "Collapse" : "Show questions"}
                          onClick={() => toggleExpanded(batch)}
                        >
                          <i
                            className={`fa fa-fw fa-caret-${
                              isOpen ? "down" : "right"
                            }`}
                          />
                        </Button>
                      </td>

                      <td>
                        {renaming?.id === batch.id ? (
                          <Input
                            autoFocus
                            bsSize="sm"
                            value={renaming.name}
                            onChange={(e) =>
                              setRenaming({
                                id: batch.id,
                                name: e.target.value,
                              })
                            }
                            onBlur={() => void finishRename(true)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") {
                                void finishRename(true);
                              }
                              if (e.key === "Escape") {
                                void finishRename(false);
                              }
                            }}
                          />
                        ) : (
                          <span className="d-inline-flex align-items-center">
                            {batch.name}
                            <Button
                              color="link"
                              className="p-0 ml-2"
                              title="Rename batch"
                              onClick={() =>
                                setRenaming({ id: batch.id, name: batch.name })
                              }
                            >
                              <i className="fas fa-pen small" />
                            </Button>
                          </span>
                        )}
                      </td>

                      <td>
                        <Badge color="light">{batch.contentType}</Badge>
                      </td>

                      <td className="small text-muted">
                        {batch.createdBy} ·{" "}
                        <DateDisplay date={new global.Date(batch.createdAt)} />
                      </td>

                      <td>{total}</td>

                      <td>
                        <div className="small">
                          {reviewed}/{total} reviewed · {batch.approvedCount}{" "}
                          approved
                        </div>
                        <Progress
                          className="mt-1"
                          style={{ height: 4 }}
                          color={fullyReviewed ? "success" : "secondary"}
                          value={total > 0 ? (reviewed / total) * 100 : 0}
                        />
                        {batch.generation && status !== "done" && (
                          <div className="small mt-1">
                            <Badge color={STATUS_COLOR[status]}>
                              {status === "running" && (
                                <i className="fas fa-circle-notch fa-spin mr-1" />
                              )}
                              {status}
                            </Badge>
                            <span className="text-muted ml-1">
                              {progress.produced}/{progress.requested}
                            </span>
                          </div>
                        )}
                        {failure && (
                          <div
                            className="small text-danger mt-1"
                            title={failure}
                          >
                            {failure}
                          </div>
                        )}
                      </td>

                      <td>
                        <Badge
                          color={
                            batch.state === "live" ? "success" : "secondary"
                          }
                        >
                          {batch.state}
                        </Badge>
                      </td>

                      <td className="text-right text-nowrap">
                        {isRetryable(status) && (
                          <Button
                            size="sm"
                            color="warning"
                            outline
                            className="mr-1"
                            disabled={busy}
                            title="Retry the remaining questions"
                            onClick={() =>
                              act(
                                batch,
                                async () => {
                                  await Content.retryBatch(batch.id);
                                  toast.success(
                                    "Retrying the remaining questions",
                                  );
                                },
                                "Could not retry",
                              )
                            }
                          >
                            Retry
                          </Button>
                        )}
                        <Button
                          size="sm"
                          color="secondary"
                          outline
                          className="mr-1"
                          tag={Link}
                          to={`/content/drafts/${batch.id}`}
                          title="Open in review"
                        >
                          Open
                        </Button>
                        {/* Not a disabled button once live: Bootstrap barely
                            dims a disabled primary, so it still reads as the
                            row's call to action when there is nothing left to
                            publish. */}
                        {batch.state === "live" ? (
                          <span className="text-success small mr-2">
                            <i className="fas fa-check mr-1" />
                            Published
                          </span>
                        ) : (
                          <Button
                            size="sm"
                            color={
                              fullyReviewed && status !== "running"
                                ? "primary"
                                : "secondary"
                            }
                            outline={!fullyReviewed || status === "running"}
                            className="mr-1"
                            disabled={
                              busy || !fullyReviewed || status === "running"
                            }
                            title={
                              status === "running"
                                ? "Generation is still running"
                                : fullyReviewed
                                ? "Publish now"
                                : "Review all questions first"
                            }
                            onClick={() =>
                              act(
                                batch,
                                async () => {
                                  const { publishedCount } =
                                    await Content.publishBatch(batch.id);
                                  toast.success(
                                    `Published ${publishedCount} question(s)`,
                                  );
                                },
                                "Could not publish",
                              )
                            }
                          >
                            Publish now
                          </Button>
                        )}
                        <Button
                          size="sm"
                          color="danger"
                          outline
                          disabled={busy || batch.state === "live"}
                          title={
                            batch.state === "live"
                              ? "Published batches cannot be deleted"
                              : "Delete draft"
                          }
                          onClick={() => setDeleting(batch)}
                        >
                          <i className="fas fa-trash" />
                        </Button>
                      </td>
                    </tr>

                    {isOpen && (
                      <tr>
                        <td colSpan={8} className="bg-light">
                          <div className="font-weight-bold small mb-2">
                            {batch.name} — questions
                          </div>
                          {expandedQuestions.length === 0 ? (
                            <div className="text-muted small">
                              No questions yet.
                            </div>
                          ) : (
                            <ul className="list-unstyled mb-2">
                              {expandedQuestions.map((question) => (
                                <li key={question.id} className="mb-1">
                                  {question.title}
                                  <Badge
                                    className="ml-2"
                                    color={
                                      REVIEW_BADGE[
                                        question.reviewStatus ?? "pending"
                                      ]
                                    }
                                  >
                                    {question.reviewStatus ?? "pending"}
                                  </Badge>
                                </li>
                              ))}
                            </ul>
                          )}

                          <div className="small">
                            <span className="text-muted mr-2">Hidden on:</span>
                            {PLATFORMS.map((platform) => {
                              const hidden =
                                batch.hiddenOnPlatforms.includes(platform);
                              return (
                                <Button
                                  key={platform}
                                  size="sm"
                                  className="mr-1"
                                  color={hidden ? "danger" : "secondary"}
                                  outline={!hidden}
                                  disabled={busy}
                                  title={
                                    hidden
                                      ? `Hidden on ${platform} — click to show`
                                      : `Visible on ${platform} — click to hide`
                                  }
                                  onClick={() =>
                                    act(
                                      batch,
                                      async () => {
                                        await Content.setBatchVisibility({
                                          batchId: batch.id,
                                          hiddenOnPlatforms: hidden
                                            ? batch.hiddenOnPlatforms.filter(
                                                (p) => p !== platform,
                                              )
                                            : [
                                                ...batch.hiddenOnPlatforms,
                                                platform,
                                              ],
                                        });
                                      },
                                      "Could not change visibility",
                                    )
                                  }
                                >
                                  {platform}
                                </Button>
                              );
                            })}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </ContentTable>
        )}
      </Card>

      {deleting && (
        <ConfirmModal
          isOpen
          title="Delete draft?"
          confirmLabel="Delete"
          confirmColor="danger"
          onConfirm={confirmDelete}
          onCancel={() => setDeleting(null)}
        >
          Delete the draft batch <strong>“{deleting.name}”</strong> and its{" "}
          {deleting.questionIds.length} generated question
          {deleting.questionIds.length === 1 ? "" : "s"}? This cannot be undone.
        </ConfirmModal>
      )}
    </>
  );
};
